<template>
  <div class="relative w-full h-full min-h-0 overflow-hidden rounded-2xl">
    <VehiclePerceptionCanvas
      ref="canvasRef"
      :obstacles="obstaclesAll"
      :vehicle-speed-kmh="vehicleSpeedKmh"
      :preview-display-range-m="domainStore.previewSceneDisplayRangeM"
    />
    <div
      v-if="domainStore.showSubscribePlaybackControl"
      class="absolute bottom-4 right-20 z-20 rounded-lg border border-vp-border bg-vp-secondary/80 p-1 backdrop-blur-sm pointer-events-auto"
    >
      <q-btn
        flat
        dense
        size="sm"
        class="!text-vp-accent min-w-9 min-h-9"
        :icon="domainStore.subscribePlaybackPlaying ? 'pause' : 'play_arrow'"
        :aria-label="
          domainStore.subscribePlaybackPlaying ? '暂停数据' : '播放数据'
        "
        @click="domainStore.toggleSubscribePlaybackPlaying()"
      />
    </div>
    <div
      class="absolute bottom-4 right-4 z-20 rounded-lg border border-vp-border bg-vp-secondary/80 p-1 backdrop-blur-sm pointer-events-auto"
    >
      <q-btn
        flat
        dense
        size="sm"
        class="!text-vp-accent min-w-9 min-h-9"
        icon="restart_alt"
        aria-label="复位视角"
        @click="resetSceneView"
      />
    </div>
    <VehiclePerceptionHud
      :obstacles="obstaclesAll"
      :vehicle-speed-kmh="vehicleSpeedKmh"
      :latitude="vehicleLat"
      :longitude="vehicleLng"
      :show-sus-imu-acc-vert-chart="domainStore.showHudSusImuAccVertChart"
      :sus-imu-acc-vert-samples="susImuAccVertSamples"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, watch, type ComponentPublicInstance } from "vue";

/** 预瞄订阅约 50ms/帧，保留约 5s 波形供 HUD 曲线 */
const SUS_IMU_ACC_VERT_BUFFER_MAX = 100;
import { useQuasar } from "quasar";
import { useDomainControlStore } from "@/stores/domainControl";
import { useCarProperty } from "@/composables/useCarProperty";
import VehiclePerceptionCanvas from "./vehiclePerception/VehiclePerceptionCanvas.vue";
import VehiclePerceptionHud from "./vehiclePerception/VehiclePerceptionHud.vue";
import type { ObstacleInfo } from "./vehiclePerception/vehiclePerceptionTypes";
import {
  SUBSCRIBE_SIGNALS,
  SEND_SIGNALS,
  generateMockPreviewInfoData,
} from "@/services/domainControl/previewInfo";
import {
  buildPreviewSceneObstacles,
  createPreviewSceneObstacleStabilizer,
} from "./helper/previewInfo";

defineOptions({
  name: "DomainControl_PreviewInfo",
});

const $q = useQuasar();
const domainStore = useDomainControlStore();

const canvasRef = ref<ComponentPublicInstance<{
  resetView: () => void;
}> | null>(null);

function resetSceneView(): void {
  canvasRef.value?.resetView();
}
const obstaclesAll = shallowRef<ObstacleInfo[]>([]);
const previewObstacleStabilizer = createPreviewSceneObstacleStabilizer();
const vehicleSpeedKmh = ref(0);
const vehicleLat = ref(40.1683718);
const vehicleLng = ref(116.573723);
const susImuAccVertSamples = ref<number[]>([]);

function pushSusImuAccVertSample (v: number): void {
  const next = susImuAccVertSamples.value.concat(v);
  if (next.length > SUS_IMU_ACC_VERT_BUFFER_MAX) {
    susImuAccVertSamples.value = next.slice(-SUS_IMU_ACC_VERT_BUFFER_MAX);
  } else {
    susImuAccVertSamples.value = next;
  }
}

function handleSubscribe(vehicleProperties: Record<string, any>): void {
  const data = vehicleProperties as DomianControl.PreviewInfoNotifyData;
  vehicleSpeedKmh.value = Number(data.VehSpd) || 0;
  const vert = Number(data.SusIMUAccVert);
  if (Number.isFinite(vert)) {
    pushSusImuAccVertSample(vert);
  }
  if (data.INS_Latitude != null) {
    vehicleLat.value = Number(data.INS_Latitude);
  }
  if (data.INS_Longitude != null) {
    vehicleLng.value = Number(data.INS_Longitude);
  }
  const raw = buildPreviewSceneObstacles(data);
  obstaclesAll.value = previewObstacleStabilizer.push(raw);
}

const { loading } = useCarProperty({
  subscribeSignals: SUBSCRIBE_SIGNALS,
  handleSubscribe,
  sendSignals: SEND_SIGNALS,
  mock: () => generateMockPreviewInfoData() as Record<string, any>,
});

watch(loading, (v) => {
  if (v) {
    $q.loading.show();
  } else {
    $q.loading.hide();
  }
});
</script>
